import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, PageHeader, Panel, RowsSkeleton, StatusBadge } from "@/components/app/kit";
import { FieldInput } from "@/components/app/FormDialog";
import { useFetch } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/i18n";

const EDITABLE = [
  { key: "commercial_name", label: "Nom commercial" }, { key: "phone", label: "Téléphone" }, { key: "email", label: "E-mail", type: "email" },
  { key: "website", label: "Site web" }, { key: "address", label: "Adresse" }, { key: "postal_code", label: "Code postal" }, { key: "city", label: "Ville" },
];

const Info = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 py-2 text-sm border-b border-slate-100 last:border-0"><span className="text-slate-500">{label}</span><span className={mono ? "font-mono text-xs" : "font-medium text-right"}>{value || "—"}</span></div>
);

export default function TenantSettings() {
  const { can, refresh } = useAuth();
  const manage = can("settings:manage");
  const { data } = useFetch("/t/settings");
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (data) setV(data);
  }, [data]);
  if (!v) return <RowsSkeleton rows={6} />;
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/t/settings", { ...Object.fromEntries(EDITABLE.map((f) => [f.key, v[f.key] || ""])), logo_data: v.logo_data || null });
      toast.success("Paramètres enregistrés");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Administration" title="Paramètres du cabinet" subtitle="Les paramètres régionaux, l'abonnement et les domaines sont gérés par l'administration de la plateforme." />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <Panel title="Coordonnées" className="lg:col-span-7">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {EDITABLE.map((f) => (
              <Field key={f.key} label={f.label}><FieldInput field={{ ...f, disabled: !manage }} value={v[f.key]} onChange={(x) => setV((s) => ({ ...s, [f.key]: x }))} testPrefix="tenant-settings" /></Field>
            ))}
          </div>
          {manage && <Button className="mt-6" onClick={save} disabled={busy} data-testid="tenant-settings-save">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>}
        </Panel>
        <div className="lg:col-span-5 space-y-6">
          <Panel title="Abonnement">
            <Info label="Formule" value={v.plan_name} />
            <div className="flex justify-between py-2 text-sm border-b border-slate-100"><span className="text-slate-500">Statut</span><StatusBadge status={v.status} /></div>
            <Info label="Période" value={`${fmtDate(v.start_date)} → ${fmtDate(v.end_date)}`} />
            <Info label="Utilisateurs max." value={v.limits?.max_users} />
            <Info label="Patients max." value={v.limits?.max_patients ?? "Illimité"} />
          </Panel>
          <Panel title="Régional et accès">
            <Info label="Identifiant" value={v.slug} mono />
            <Info label="Devise · Langue" value={`${v.currency} · ${v.language}`} />
            <Info label="Fuseau horaire" value={v.timezone} />
            {v.domains.map((d) => <Info key={d.id} label={d.type === "custom" ? "Domaine" : "Sous-domaine"} value={`${d.hostname} (${d.status === "verified" ? "vérifié" : "en attente"})`} mono />)}
          </Panel>
        </div>
      </div>
    </div>
  );
}
